import React, { useEffect, useRef, useState } from 'react';
import { ApplicationView, useController } from '@aha-app/mvc';
import WhiteboardController from '../../controllers/WhiteboardController';
import { type Instance as TippyInstance } from 'tippy.js/index';
import Tool, { type ToolButtonComponent } from '../Tool';
import ToolWireframe from './ToolWireframe';
import ToolbarMenu from '../../toolbars/ToolbarMenu';
import ToolButton from '../../toolbars/ToolButton';
import { svgToIcon } from '../../utils/svg';
import type Wireframe from '../../objects/Wireframe';
import { camelToSentenceCase } from '../../utils/string';
import { startCase } from 'lodash';
import { type ComponentStyles } from '../../wireframes/WireframeBase';

interface PopupProps {
  setCurrentStyle: (style: Wireframe['style']) => void;
  selection: fabric.Object;
  hidePopup: () => void;
}

const styleTitle = (style: string) => {
  // Special handling for stylized style titles
  if (style.includes('_')) {
    return startCase(style).split(' ').join(' / ');
  } else {
    return camelToSentenceCase(style);
  }
};

const Popup: React.FC<PopupProps> = ApplicationView(
  ({ setCurrentStyle, selection, hidePopup }) => {
    const controller = useController(WhiteboardController);
    const states = selection
      .firstObject()
      .wireframe.componentStyles() as ComponentStyles;
    const entries = Object.entries(states);

    const setState = (style: Wireframe['style']) => {
      setCurrentStyle(style);
      selection.setOnObjects('style', style);
      hidePopup();
      Tool.toolActionComplete({ controller });
    };

    return (
      <div>
        <div style={{ maxHeight: 200, overflow: 'auto' }}>
          <ToolbarMenu columns={entries.length}>
            {Object.entries(states).map(([style, icon]) => (
              <ToolButton
                key={style}
                onClick={() => setState(style)}
                name={styleTitle(style)}
              >
                {svgToIcon(icon)}
              </ToolButton>
            ))}
          </ToolbarMenu>
        </div>
      </div>
    );
  }
);

const StyleButton: ToolButtonComponent = ApplicationView(
  ({ selection, tool }) => {
    const tippyRef = useRef<TippyInstance>();
    const [currentStyle, setCurrentStyle] = useState(
      selection.firstObject().wireframe.componentStyle
    );
    const styles = selection.firstObject().wireframe.componentStyles();

    useEffect(() => {
      const handleSelectionModified = () => {
        const newStyle = selection.firstObject().wireframe.componentStyle;
        if (newStyle !== currentStyle) {
          setCurrentStyle(selection.firstObject().wireframe.componentStyle);
        }
      };

      selection.on('modified', handleSelectionModified);
      return () => {
        selection.off('modified', handleSelectionModified);
      };
    }, [currentStyle, selection]);

    return (
      <ToolButton
        popup={
          <Popup
            setCurrentStyle={setCurrentStyle}
            selection={selection}
            hidePopup={() => tippyRef.current?.hide()}
          />
        }
        tool={tool}
        tippyRef={tippyRef}
      >
        {svgToIcon(styles[currentStyle])}
      </ToolButton>
    );
  }
);

class ToolWireframeComponentStyle extends ToolWireframe {
  static toolName = 'Style';

  static buttonComponent() {
    return StyleButton;
  }
}

export default ToolWireframeComponentStyle;
